import Backdrop from '@/components/ui/backdrop';
import { Location, SingleSite as Site } from '@/core/entities';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { Box, Paper } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SiteForm from '@/features/sites/site-form';
import DeleteSiteForm from '@/features/sites/DeleteSiteForm';
import { useCustomer } from '@/queries/customers';
import DeleteLocationForm from '@/features/locations/DeleteLocation';
import UpdateLocationForm, {
	UpdateLocationFormModel,
} from '@/features/locations/UpdateLocationForm';
import { useLevelLocations } from '@/queries/locations';
import { useSite } from '@/queries/sites';
import { PAGINATION_META_DEFAULT } from '@/shared/constants/pagination';
import { PaginationMeta } from '@/shared/types/pagination';
import AdminControls from '@/features/admin/AdminControls';
import RootLocationManagement from '@/features/locations/locations-management/RootLocationsManagement';

const SitePage: FunctionComponent = (): ReactElement => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { customerId, siteId } = useParams();

	const [siteForm, setSiteForm] = useState({ open: false });
	const [deleteSiteForm, setDeleteSiteForm] = useState<{ open: boolean; site?: Site }>({
		open: false,
	});
	const [deleteLocationForm, setDeleteLocationForm] = useState<{
		open: boolean;
		location?: Location;
	}>({ open: false });
	const [updateLocationForm, setUpdateLocationForm] = useState<{
		open: boolean;
		formModel: UpdateLocationFormModel;
		location?: Location;
	}>({
		open: false,
		formModel: { name: '' },
	});

	const [locations, setLocations] = useState<Location[]>([]);
	const [, setPaginationMeta] = useState<PaginationMeta>(PAGINATION_META_DEFAULT);

	const {
		query: { data: customer },
	} = useCustomer(customerId);
	const {
		queryKey: useSiteQueryKey,
		query: { data: site },
	} = useSite(siteId);

	const rootLevel = useMemo(() => site?.levels.find((level) => level.parent === null), [site]);

	const {
		queryKey: levelLocationsQueryKey,
		query: { isLoading: isLevelLocationsLoading, data: levelLocationsPages },
	} = useLevelLocations({
		level: rootLevel,
		queryParams: { limit: 99999 },
	});

	useEffect(() => {
		const { locations } = levelLocationsPages?.pages[0] || {};

		if (locations) {
			const { totalItems, totalPages, page: currentPage, itemsPerPage: pageSize } = locations;
			setLocations(locations.data);
			setPaginationMeta({ totalItems, totalPages, currentPage, pageSize });
		}
	}, [levelLocationsPages]);

	const refetchLocations = async () => {
		await queryClient.invalidateQueries(levelLocationsQueryKey);
	};

	const refetchSite = async () => {
		await queryClient.invalidateQueries(useSiteQueryKey);
	};

	if (!site || !customer || !rootLevel) {
		return <Backdrop />;
	}

	return (
		<Fragment>
			<AdminPanelPage
				title={site.name}
				breadcrumbs={[
					{ title: 'Customers', path: '/customers' },
					{ title: customer.name, path: `/customers/${customerId!}/site-management` },
					{ title: site.name },
				]}
				headerControls={
					<AdminControls
						remove={() => setDeleteSiteForm({ site, open: true })}
						edit={() => setSiteForm({ ...siteForm, open: true })}
					/>
				}
			>
				<Box component={Paper} padding={3}>
					<RootLocationManagement
						site={site}
						level={rootLevel}
						header={{
							controlLocationsTemplate: true,
						}}
						table={{
							data: locations,
							isLoading: isLevelLocationsLoading,
							rowAction: (location) =>
								navigate(`/customers/${customer.id}/locations/${location.id}`),
							columnActions: {
								edit: (location) =>
									setUpdateLocationForm({
										...updateLocationForm,
										location,
										formModel: { name: location.name },
										open: true,
									}),
								remove: (location) =>
									setDeleteLocationForm({ ...deleteLocationForm, location, open: true }),
							},
						}}
						invalidateData={refetchLocations}
					/>
				</Box>
			</AdminPanelPage>
			<SiteForm
				open={siteForm.open}
				onClose={() => setSiteForm({ open: false })}
				customer={customer}
				site={site}
				subtitle={customer.name}
				onSubmitted={refetchSite}
			/>
			<DeleteSiteForm
				open={deleteSiteForm.open}
				site={deleteSiteForm.site}
				onClose={() => setDeleteSiteForm({ ...deleteSiteForm, open: false })}
				onSubmitted={() => {
					/* eslint-disable @typescript-eslint/no-floating-promises */
					refetchLocations();
					navigate(`/customers/${customerId!}/site-management`, { replace: true });
				}}
				customerName={customer.name}
			/>
			<UpdateLocationForm
				open={updateLocationForm.open}
				onClose={() => setUpdateLocationForm({ ...updateLocationForm, open: false })}
				location={updateLocationForm.location}
				level={rootLevel}
				formModel={updateLocationForm.formModel}
				onChangeFormModel={(field: keyof Location, value: string) =>
					setUpdateLocationForm({
						...updateLocationForm,
						formModel: { ...updateLocationForm.formModel, [field]: value },
					})
				}
				onSubmitted={refetchLocations}
			/>
			<DeleteLocationForm
				open={deleteLocationForm.open}
				onClose={() => setDeleteLocationForm({ ...deleteLocationForm, open: false })}
				location={deleteLocationForm.location}
				level={rootLevel}
				onSubmitted={refetchLocations}
			/>
		</Fragment>
	);
};

export default SitePage;
